import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import TextInput from '@common/components/Inputs/TextInput';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { DeleteIcon } from '@common/utils/icons/icons';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectTorizonVersionSets } from '../../store/general/general-slice-selectors';
import { resetTorizonVersionSetsRestState } from '../../store/general/general-slice';
import {
  createTorizonVersionSet,
  deleteTorizonVersionSetById,
  patchTorizonVersionSetById,
} from '../../store/general/general-actions';
import TorizonVersionSet from '../../models/general/versions/torizon-version-set';
import TorizonVersionsForm from './TorizonVersionsForm';
import CustomConfirmAbortDialog from '../misc/CustomConfirmAbortDialog';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';

const TorizonVersionSetForm: React.FC<{
  versionSet: Partial<TorizonVersionSet>;
  onClose: () => void;
}> = ({ versionSet, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const torizonSets = useAppSelector((state) => selectTorizonVersionSets(state));

  const [name, setName] = useState(versionSet.name ?? '');
  const [isDefault, setIsDefault] = useState(versionSet.isDefault ?? false);
  const [osPackageId, setOsPackageId] = useState(versionSet.osPackageId ?? '');
  const [dockerPackageId, setDockerPackageId] = useState(versionSet.dockerPackageId ?? '');
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);

  const hasSubmitted = useRef(false);

  useEffect(() => {
    if (
      hasSubmitted.current &&
      (torizonSets.rest.postStatus === HttpFetchStatus.Success ||
        torizonSets.rest.patchStatus === HttpFetchStatus.Success ||
        torizonSets.rest.deleteStatus === HttpFetchStatus.Success)
    ) {
      hasSubmitted.current = false;
      onClose();
    }
  }, [
    onClose,
    torizonSets.rest.postStatus,
    torizonSets.rest.patchStatus,
    torizonSets.rest.deleteStatus,
  ]);

  const closeHandler = () => {
    hasSubmitted.current = false;
    dispatch(
      resetTorizonVersionSetsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
    onClose();
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    hasSubmitted.current = true;
    const data = { name, isDefault, osPackageId, dockerPackageId };
    if (versionSet.id != null) {
      dispatch(patchTorizonVersionSetById({ id: versionSet.id, data }));
    } else {
      dispatch(createTorizonVersionSet(data));
    }
  };

  const deleteClickHandler = () => {
    if (versionSet.id != null) {
      hasSubmitted.current = true;
      dispatch(deleteTorizonVersionSetById(versionSet.id));
      setIsConfirmDeleteDialogOpen(false);
    }
  };

  const packageChangeHandler =
    (setValue: (value: string) => void) => (event: SelectChangeEvent<unknown>) =>
      setValue((event.target.value as string) ?? '');

  const robotCount = versionSet.robotCount ?? 0;
  const packagesChanged =
    osPackageId !== (versionSet.osPackageId ?? '') ||
    dockerPackageId !== (versionSet.dockerPackageId ?? '');

  // Mirrors the serializer's all-or-none rule, so a half-filled set is caught before
  // the round-trip rather than after it.
  const isPartial =
    [osPackageId, dockerPackageId].some((id) => id !== '') &&
    !(osPackageId !== '' && dockerPackageId !== '');

  const isSaving =
    torizonSets.rest.postStatus === HttpFetchStatus.Loading ||
    torizonSets.rest.patchStatus === HttpFetchStatus.Loading;

  return (
    <React.Fragment>
      <DialogWrapperWithClose
        open
        onClose={closeHandler}
        maxWidth="sm"
        fullWidth
        title={
          versionSet.id == null
            ? t('title-create-torizon-version-set')
            : t('title-update-torizon-version-set')
        }
      >
        <form onSubmit={formSubmitHandler}>
          <DialogContent>
            <Stack mt={2} spacing={2}>
              {robotCount > 0 && (
                <CustomAlert
                  severity="warning"
                  header={t('warning-torizon-version-set-affects-robots', { count: robotCount })}
                />
              )}
              {packagesChanged && (
                <CustomAlert severity="info" header={t('warning-torizon-version-set-rename')} />
              )}
              {isPartial && (
                <CustomAlert
                  severity="error"
                  header={t('validation-error-incomplete-package-set')}
                />
              )}

              <TextInput
                value={name}
                onChange={(event) => setName(event.target.value)}
                validationResult={{ isValid: name.length > 0, error: null }}
                label={t('input-label-torizon-version-set-name')}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isDefault}
                    onChange={(event) => setIsDefault(event.target.checked)}
                    data-testid="switch-torizon-version-set-default"
                  />
                }
                label={t('input-label-torizon-version-set-default')}
              />

              {/* TODO-torizon-bootloader: Torizon has no separate bootloader package - it
                  ships inside the OSTREE os image. Re-add the picker here, and the field
                  to TORIZON_PACKAGE_FIELDS, once Torizon publishes one. */}

              <TorizonVersionsForm
                label={t('input-label-torizon-os')}
                initialValueId={versionSet.osPackageId ?? undefined}
                value={osPackageId}
                onChange={packageChangeHandler(setOsPackageId)}
                validationResult={{ isValid: true, error: null }}
                packageType="OSTREE"
              />

              <TorizonVersionsForm
                label={t('input-label-torizon-docker')}
                initialValueId={versionSet.dockerPackageId ?? undefined}
                value={dockerPackageId}
                onChange={packageChangeHandler(setDockerPackageId)}
                validationResult={{ isValid: true, error: null }}
                packageType="BINARY"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                data-testid="button-save-torizon-version-set"
                disabled={isSaving || name.length === 0 || isPartial}
                type="submit"
              >
                {isSaving ? (
                  <CircularProgress size={24} />
                ) : versionSet.id == null ? (
                  t('button-add-version')
                ) : (
                  t('button-save')
                )}
              </Button>
              {versionSet.id != null && (
                <TooltipIconButton
                  icon={<DeleteIcon color={robotCount > 0 ? 'disabled' : 'error'} />}
                  onClick={() => setIsConfirmDeleteDialogOpen(true)}
                  tooltip={
                    robotCount > 0
                      ? t('info-tooltip-torizon-version-set-in-use')
                      : t('info-tooltip-delete')
                  }
                  disabled={robotCount > 0}
                  data-testid="button-delete-torizon-version-set"
                />
              )}
            </Stack>
          </DialogActions>
        </form>
      </DialogWrapperWithClose>
      <CustomConfirmAbortDialog
        open={isConfirmDeleteDialogOpen}
        title={t('title-delete-torizon-version-set')}
        text={<CustomAlert severity="warning" header={t('warning-deleting-is-permanent')} />}
        confirmButton={t('button-delete')}
        abortButton={t('button-cancel')}
        onAbort={() => setIsConfirmDeleteDialogOpen(false)}
        onConfirm={deleteClickHandler}
      />
    </React.Fragment>
  );
};

export default TorizonVersionSetForm;
